import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { BillingSoftwareFields } from '@/components/layout/contact/billing-software-fields';
import { Field } from '@/components/layout/contact/field';
import { PhoneField } from '@/components/layout/contact/phone-field';
import { MOTION, formatPhoneForSubmit } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { getRecaptchaToken, submitContactForm } from '@/services/contact.service';
import {
  INITIAL_FORM,
  type ContactFormData,
  validateContactForm,
  type FormErrors,
} from '@/utils/contact-form.utils';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import Script from 'next/script';
import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────
// 🧪 SIMULATION MODE
// Mettre à `true` pour tester la gestion d'erreurs sans
// spammer l'endpoint réel. En mode simulation :
//   - les erreurs de validation s'affichent normalement
//   - si tout est valide → redirection vers /contact-success
//   - aucun appel réseau n'est effectué
// ─────────────────────────────────────────────────────────────
const IS_SIMULATION = false;

const CONTACT_SEO: SEOProps = {
  title: 'Contact | zLawyer',
  description:
    'Contactez zLawyer pour une démo ou obtenir des informations sur notre solution de gestion et facturation pour cabinets d’avocats.',
  url: 'https://www.zlawyer.fr/logiciel-avocats/contact/',
};

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export const getStaticProps: GetStaticProps<{
  recaptchaSiteKey: string | null;
}> = async () => ({
  props: {
    recaptchaSiteKey: process.env.RECAPTCHA_SITE_KEY ?? null,
  },
});

export default function Contact({
  recaptchaSiteKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef<HTMLDivElement>(null),
    title: useRef<HTMLHeadingElement>(null),
    intro: useRef<HTMLParagraphElement>(null),
    orbit: useRef<HTMLDivElement>(null),
    infos: useRef<HTMLDivElement>(null),
    form: useRef<HTMLDivElement>(null),
  };

  useGSAP(
    () => {
      if (!refs.section.current || !refs.title.current || !refs.intro.current) return;

      const splitTitle = new SplitText(refs.title.current, { type: 'lines', mask: 'lines' });
      const splitIntro = new SplitText(refs.intro.current, { type: 'lines', mask: 'lines' });

      const infoItems = refs.infos.current
        ? gsap.utils.toArray<HTMLElement>(':scope > *', refs.infos.current)
        : [];
      const fieldItems = refs.form.current
        ? gsap.utils.toArray<HTMLElement>('form > *', refs.form.current)
        : [];

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });
      gsap.set(refs.orbit.current, { autoAlpha: 0, scale: 0.92 });
      gsap.set(infoItems, { autoAlpha: 0, y: 10 });
      gsap.set(fieldItems, { autoAlpha: 0, y: 12 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.15',
        )
        .from(
          splitIntro.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.2',
        )
        .to(
          refs.orbit.current,
          { autoAlpha: 1, scale: 1, duration: MOTION.durationMd, ease: MOTION.ease },
          '<+0.1',
        )
        .to(
          infoItems,
          {
            autoAlpha: 1,
            y: 0,
            duration: MOTION.durationSm,
            ease: MOTION.ease,
            stagger: 0.08,
          },
          '<+0.05',
        )
        .to(
          fieldItems,
          {
            autoAlpha: 1,
            y: 0,
            duration: MOTION.durationSm,
            ease: MOTION.ease,
            stagger: 0.06,
          },
          '<',
        );

      return () => {
        splitTitle.revert();
        splitIntro.revert();
      };
    },
    { scope: refs.section },
  );

  const handleChange = (event: React.ChangeEvent<{ name: string; value: string }>) => {
    const name = event.target.name as keyof ContactFormData;
    const { value } = event.target;

    const nextForm = { ...form, [name]: value } as ContactFormData;
    setForm(nextForm);

    // Revalidation live uniquement apres une premiere tentative de submit,
    // pour laisser l'erreur disparaitre des que l'utilisateur corrige le champ.
    if (hasSubmitted) {
      const fieldErrors = validateContactForm(nextForm);
      setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    setGlobalError(null);
    setHasSubmitted(true);

    const validationErrors = validateContactForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    if (IS_SIMULATION) {
      setIsSubmitting(true);
      await new Promise((r) => setTimeout(r, 800));
      setIsSubmitting(false);
      await router.push('/contact-success?from=contact', undefined, { scroll: false });
      return;
    }

    if (!recaptchaSiteKey) {
      setGlobalError('La clé reCAPTCHA est introuvable dans la configuration.');
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey);
      await submitContactForm(
        {
          ...form,
          telephone: formatPhoneForSubmit(form.telephone),
        },
        recaptchaToken,
      );

      setForm(INITIAL_FORM);
      await router.push('/contact-success?from=contact', undefined, { scroll: false });
    } catch (error) {
      setGlobalError(
        error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {!IS_SIMULATION && recaptchaSiteKey ? (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
          strategy="afterInteractive"
        />
      ) : null}

      <section ref={refs.section} className="max-width-blue-section" id="contact">
        <div className="border-dark-blue/6 mx-auto grid w-full max-w-6xl overflow-hidden rounded-(--radius-current) border bg-white shadow-(--shadow-current) md:grid-cols-[1fr_1.1fr]">
          {/* ─── Colonne gauche — présentation ─────────────────────────── */}
          <div className="relative flex flex-col justify-between gap-8 p-6 sm:gap-10 sm:p-8 md:p-10 lg:p-12">
            <div className="flex flex-col gap-6 sm:gap-8">
              <Tag ref={refs.tag}>Contact</Tag>

              <div className="flex flex-col gap-4">
                <h2 ref={refs.title} className="text-left">
                  Parlons de votre cabinet
                </h2>
                <p
                  ref={refs.intro}
                  aria-hidden="true"
                  className="text-dark-blue/75 max-w-md leading-relaxed"
                >
                  Démo, reprise de données ou simple question : répondez en quelques champs, notre
                  équipe revient vers vous rapidement.
                </p>
                <p className="sr-only">
                  Démo, reprise de données ou simple question : répondez en quelques champs, notre
                  équipe revient vers vous rapidement.
                </p>
              </div>
            </div>

            <div
              ref={refs.orbit}
              aria-hidden="true"
              className="relative mx-auto hidden h-32 w-32 items-center justify-center md:flex lg:h-44 lg:w-44"
            >
              <div className="bg-orange/5 absolute inset-4 rounded-full lg:inset-6" />
              <div className="h-10 w-10 lg:h-14 lg:w-14">
                <Image
                  alt=""
                  className="h-full w-full object-contain"
                  decoding="async"
                  height={60}
                  src={withBasePath('/images/adaptative/zlawyer.svg')}
                  width={180}
                />
              </div>
            </div>

            <div ref={refs.infos} className="flex flex-col gap-4 text-sm">
              <a
                className="group text-dark-blue/80 hover:text-orange w-fit transition-colors duration-200"
                href="tel:+33184257027"
              >
                <span className="font-roboto-medium">01 84 25 70 27</span>
              </a>

              <div className="bg-dark-blue/10 h-px w-full" />
              <a
                className="group text-dark-blue/80 hover:text-orange w-fit transition-colors duration-200"
                href="mailto:info@zele-solutions.fr"
              >
                <span className="font-roboto-medium">info@zele-solutions.fr</span>
              </a>
            </div>
          </div>

          {/* ─── Colonne droite — formulaire ────────────────────────────── */}
          <div className="border-dark-blue/[0.07] border-t p-6 sm:p-8 md:border-t-0 md:border-l md:p-10 lg:p-12">
            {globalError && (
              <div
                aria-live="assertive"
                className="mb-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <svg className="mt-0.5 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    clipRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    fillRule="evenodd"
                  />
                </svg>
                {globalError}
              </div>
            )}

            {IS_SIMULATION && (
              <div className="mb-6 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                <span className="font-semibold">🧪 Mode simulation activé</span> — aucun email ne
                sera envoyé.
              </div>
            )}

            <div ref={refs.form}>
              <form
                className="grid grid-cols-2 gap-x-3 gap-y-6 sm:gap-x-6 sm:gap-y-8"
                method="POST"
                noValidate
                onSubmit={handleSubmit}
              >
                <Field
                  colSpan={2}
                  error={errors.cabinet}
                  label="Nom du cabinet"
                  name="cabinet"
                  value={form.cabinet}
                  required
                  onChange={handleChange}
                />

                <Field
                  colSpan={2}
                  error={errors.usersCount}
                  label="Nombre d'utilisateurs"
                  min={1}
                  name="usersCount"
                  type="number"
                  value={form.usersCount}
                  required
                  onChange={handleChange}
                />

                <Field
                  error={errors.nom}
                  label="Nom"
                  name="nom"
                  value={form.nom}
                  required
                  onChange={handleChange}
                />

                <Field
                  error={errors.prenom}
                  label="Prénom"
                  name="prenom"
                  value={form.prenom}
                  required
                  onChange={handleChange}
                />

                <Field
                  error={errors.email}
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  required
                  onChange={handleChange}
                />

                <PhoneField
                  error={errors.telephone}
                  label="Téléphone"
                  value={form.telephone}
                  onChange={handleChange}
                />

                <BillingSoftwareFields form={form} onChange={handleChange} />

                <Field
                  as="textarea"
                  colSpan={2}
                  label="Message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                />

                <div className="col-span-full pt-2 sm:pt-4">
                  <Button
                    className="w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Envoi en cours…' : 'Envoyer ma demande'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

Contact.seo = CONTACT_SEO;
